'use client';

import { useState, useMemo, useEffect } from 'react';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

const getCategoryImg = (img) => {
    if (!img) return null;
    if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) {
        return img;
    }
    const cleanPath = img.startsWith('/') ? img.slice(1) : img;
    return `${BASE_URL}${cleanPath}`;
};

export default function CivilCategoryHeader({
    mainCategories = [],
    subCategories = [],
    selectedMainCatId = 'all',
    onSelectMainCategory,
    selectedSubCatId = 'all',
    onSelectSubCategory,
    subCategoryCounts = {},
    mainCategoryCounts = {},
    totalCount = 0,
    cityName = '',
}) {
    // Modal state: null | 'main' | 'sub'
    const [activeModal, setActiveModal] = useState(null);
    const [modalSearch, setModalSearch] = useState('');

    // Selected object lookups
    const selectedMainCategory = mainCategories.find(c => String(c.id) === String(selectedMainCatId));
    const selectedSubCategory = subCategories.find(sc => String(sc.id) === String(selectedSubCatId));

    // Filter subcategories that belong to the selected main category
    const activeSubCategories = selectedMainCatId === 'all'
        ? subCategories
        : subCategories.filter(sc => String(sc.main_category_id) === String(selectedMainCatId));

    // Open modal helper
    const handleOpenModal = (type) => {
        setModalSearch('');
        setActiveModal(type);
    };

    // Handle Escape key to close modal
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && activeModal) {
                setActiveModal(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeModal]);

    // Filtered & Alphabetically Grouped Main Categories for Modal
    const groupedMainCategories = useMemo(() => {
        let list = [...mainCategories];
        if (modalSearch.trim()) {
            const q = modalSearch.toLowerCase().trim();
            list = list.filter(c => (c.category_name || '').toLowerCase().includes(q));
        }
        list.sort((a, b) => (a.category_name || '').localeCompare(b.category_name || ''));

        const groups = {};
        list.forEach((item) => {
            const name = (item.category_name || '').trim();
            const firstChar = name ? name.charAt(0).toUpperCase() : '#';
            const letter = /[A-Z]/.test(firstChar) ? firstChar : '#';
            if (!groups[letter]) groups[letter] = [];
            groups[letter].push(item);
        });
        return groups;
    }, [mainCategories, modalSearch]);

    // Filtered & Alphabetically Grouped Sub Categories for Modal
    const groupedSubCategories = useMemo(() => {
        let list = [...activeSubCategories];
        if (modalSearch.trim()) {
            const q = modalSearch.toLowerCase().trim();
            list = list.filter(sc =>
                (sc.product_category_name || '').toLowerCase().includes(q) ||
                (sc.description || '').toLowerCase().includes(q)
            );
        }
        list.sort((a, b) => (a.product_category_name || '').localeCompare(b.product_category_name || ''));

        const groups = {};
        list.forEach((item) => {
            const name = (item.product_category_name || '').trim();
            const firstChar = name ? name.charAt(0).toUpperCase() : '#';
            const letter = /[A-Z]/.test(firstChar) ? firstChar : '#';
            if (!groups[letter]) groups[letter] = [];
            groups[letter].push(item);
        });
        return groups;
    }, [activeSubCategories, modalSearch]);

    const totalFilteredCount = activeModal === 'main'
        ? Object.values(groupedMainCategories).reduce((acc, curr) => acc + curr.length, 0)
        : Object.values(groupedSubCategories).reduce((acc, curr) => acc + curr.length, 0);

    const availableLetters = Object.keys(activeModal === 'main' ? groupedMainCategories : groupedSubCategories);

    return (
        <section className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white py-6 md:py-8 px-4 border-b border-slate-700/50 shadow-lg">
            <div className="container-custom">
                {/* Header Title & Category Selectors Row */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/40 text-primary-300 text-xs font-semibold tracking-wide uppercase mb-2">
                            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                            Verified Civil Marketplace
                        </div>
                        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                            Civil Services & Construction Materials {cityName ? <span className="text-primary-400">in {cityName}</span> : ''}
                        </h1>
                        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
                            Explore verified suppliers, contractors, and building material providers in your region.
                        </p>
                    </div>

                    {/* Main & Sub Category Clickable Trigger Buttons (Same row in Mobile & Desktop) */}
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:items-center sm:gap-3 w-full lg:w-auto shrink-0">
                        {/* Main Category Trigger Button */}
                        <button
                            id="header-main-category-trigger"
                            type="button"
                            onClick={() => handleOpenModal('main')}
                            className="category-dropdown-pulse-primary relative w-full sm:w-auto sm:min-w-[210px] md:min-w-[230px] px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-slate-800/95 text-white text-left flex items-center justify-between gap-1.5 sm:gap-3 shadow-lg cursor-pointer hover:bg-slate-750 transition-all duration-300 group"
                        >
                            <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0 flex-1">
                                <span className="text-primary-400 text-sm sm:text-base shrink-0">🏢</span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[9px] sm:text-[10px] uppercase font-bold text-slate-400 tracking-wider">Main Category</div>
                                    <div className="text-[11px] sm:text-xs md:text-sm font-semibold truncate text-white">
                                        {selectedMainCategory ? selectedMainCategory.category_name : 'All Categories'}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <span className="text-[10px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full bg-primary/20 text-primary-300">
                                    {selectedMainCatId === 'all' ? totalCount : (mainCategoryCounts[selectedMainCatId] ?? 0)}
                                </span>
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 group-hover:text-white transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>

                        {/* Sub Category Trigger Button */}
                        <button
                            id="header-sub-category-trigger"
                            type="button"
                            onClick={() => handleOpenModal('sub')}
                            className="category-dropdown-pulse-amber relative w-full sm:w-auto sm:min-w-[210px] md:min-w-[230px] px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-slate-800/95 text-white text-left flex items-center justify-between gap-1.5 sm:gap-3 shadow-lg cursor-pointer hover:bg-slate-750 transition-all duration-300 group"
                        >
                            <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0 flex-1">
                                <span className="text-amber-400 text-sm sm:text-base shrink-0">🏷️</span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[9px] sm:text-[10px] uppercase font-bold text-slate-400 tracking-wider">Sub Category</div>
                                    <div className="text-[11px] sm:text-xs md:text-sm font-semibold truncate text-white">
                                        {selectedSubCategory ? selectedSubCategory.product_category_name : 'All Subcategories'}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <span className="text-[10px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                                    {selectedSubCatId === 'all'
                                        ? (selectedMainCatId === 'all' ? totalCount : (mainCategoryCounts[selectedMainCatId] ?? 0))
                                        : (subCategoryCounts[selectedSubCatId] ?? 0)}
                                </span>
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 group-hover:text-white transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>
                    </div>
                </div>
                        {/* CATEGORY SELECTION POPUP MODAL (Bright / Light Theme) */}
            {activeModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/60 backdrop-blur-sm animate-fadeIn"
                    onClick={() => setActiveModal(null)}
                >
                    <div
                        className="bg-white border border-slate-200 rounded-2xl w-full max-w-xl max-h-[88vh] flex flex-col shadow-2xl overflow-hidden animate-scaleUp"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="p-4 md:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/90">
                            <div className="flex items-center gap-2.5">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${
                                    activeModal === 'main' ? 'bg-primary/15 text-primary' : 'bg-amber-500/15 text-amber-600'
                                }`}>
                                    {activeModal === 'main' ? '🏢' : '🏷️'}
                                </div>
                                <div>
                                    <h3 className="text-base md:text-lg font-bold text-slate-900">
                                        {activeModal === 'main' ? 'Select Main Category' : 'Select Sub Category'}
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        {activeModal === 'main'
                                            ? `${mainCategories.length} main categories (Alphabetical A–Z)`
                                            : selectedMainCategory
                                            ? `In ${selectedMainCategory.category_name} (${activeSubCategories.length} options)`
                                            : `${subCategories.length} sub-categories (Alphabetical A–Z)`}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setActiveModal(null)}
                                className="w-8 h-8 rounded-lg bg-slate-200/70 hover:bg-slate-200 text-slate-600 hover:text-slate-900 flex items-center justify-center transition-colors text-sm font-bold"
                                aria-label="Close modal"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Search Bar */}
                        <div className="p-4 border-b border-slate-100 bg-white">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    value={modalSearch}
                                    onChange={(e) => setModalSearch(e.target.value)}
                                    placeholder={activeModal === 'main' ? 'Search main categories (e.g. Electrical, Steel, Cement)...' : 'Search sub-categories...'}
                                    className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100/80 focus:bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-xs transition-all"
                                />
                                {modalSearch && (
                                    <button
                                        onClick={() => setModalSearch('')}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-700 text-xs"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            {/* Alphabet Quick Jump Letters */}
                            {availableLetters.length > 1 && (
                                <div className="flex items-center gap-1 mt-3 overflow-x-auto scrollbar-hide py-0.5">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 shrink-0">
                                        Jump to:
                                    </span>
                                    {availableLetters.map((letter) => (
                                        <a
                                            key={letter}
                                            href={`#cat-group-${letter}`}
                                            className="w-5 h-5 rounded-md bg-slate-100 hover:bg-primary hover:text-white border border-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 transition-all"
                                        >
                                            {letter}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Modal Category Items List grouped alphabetically */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[55vh] scrollbar-thin scroll-smooth bg-slate-50/40">
                            {/* "All" Option at Top */}
                            {!modalSearch && (
                                <button
                                    onClick={() => {
                                        if (activeModal === 'main') {
                                            onSelectMainCategory('all');
                                            onSelectSubCategory('all');
                                        } else {
                                            onSelectSubCategory('all');
                                        }
                                        setActiveModal(null);
                                    }}
                                    className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                                        (activeModal === 'main' ? selectedMainCatId === 'all' : selectedSubCatId === 'all')
                                            ? 'bg-primary/10 border-primary text-slate-900 shadow-sm ring-2 ring-primary/30'
                                            : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50 hover:border-slate-300 shadow-xs'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-lg border border-slate-200">
                                            {activeModal === 'main' ? '🏢' : '🏷️'}
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-slate-900">
                                                {activeModal === 'main' ? 'All Categories' : 'All Subcategories'}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {activeModal === 'main'
                                                    ? 'View all civil suppliers & materials'
                                                    : selectedMainCategory
                                                    ? `All sub-items in ${selectedMainCategory.category_name}`
                                                    : 'All civil sub-items'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                            {activeModal === 'main' ? totalCount : (selectedMainCatId === 'all' ? totalCount : (mainCategoryCounts[selectedMainCatId] || 0))}
                                        </span>
                                        {(activeModal === 'main' ? selectedMainCatId === 'all' : selectedSubCatId === 'all') && (
                                            <span className="text-primary font-bold text-sm">✓</span>
                                        )}
                                    </div>
                                </button>
                            )}

                            {/* Main Categories Grouped by Letter */}
                            {activeModal === 'main' && (
                                <>
                                    {totalFilteredCount === 0 ? (
                                        <div className="p-8 text-center text-slate-500 text-sm">
                                            <p className="text-2xl mb-1">🔍</p>
                                            No main categories found matching "<span className="text-slate-900 font-semibold">{modalSearch}</span>"
                                        </div>
                                    ) : (
                                        Object.entries(groupedMainCategories).map(([letter, items]) => (
                                            <div key={letter} id={`cat-group-${letter}`} className="space-y-2">
                                                {/* Alphabet Section Header (Borderless Bright) */}
                                                <div className="sticky top-0 z-10 flex items-center gap-2 py-1.5 px-1 bg-white/95 backdrop-blur-md">
                                                    <span className="w-5 h-5 rounded-md bg-primary/15 text-primary flex items-center justify-center text-[11px] font-black">
                                                        {letter}
                                                    </span>
                                                    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                                                        {letter} ({items.length})
                                                    </span>
                                                </div>

                                                {/* Items in this letter group */}
                                                <div className="space-y-2">
                                                    {items.map((mc) => {
                                                        const isSelected = String(selectedMainCatId) === String(mc.id);
                                                        const catImg = getCategoryImg(mc.category_img);
                                                        const count = mainCategoryCounts[mc.id] ?? 0;

                                                        return (
                                                            <button
                                                                key={mc.id}
                                                                onClick={() => {
                                                                    onSelectMainCategory(mc.id);
                                                                    onSelectSubCategory('all');
                                                                    setActiveModal(null);
                                                                }}
                                                                className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all group ${
                                                                    isSelected
                                                                        ? 'bg-primary/10 border-primary text-slate-900 shadow-sm ring-2 ring-primary/30'
                                                                        : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50 hover:border-slate-300 shadow-xs'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-3 min-w-0">
                                                                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-100 shrink-0 border border-slate-200 flex items-center justify-center">
                                                                        {catImg ? (
                                                                            <img
                                                                                src={catImg}
                                                                                alt={mc.category_name}
                                                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                                                                onError={(e) => {
                                                                                    e.currentTarget.style.display = 'none';
                                                                                    if (e.currentTarget.nextSibling) {
                                                                                        e.currentTarget.nextSibling.style.display = 'flex';
                                                                                    }
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <div
                                                                            style={{ display: catImg ? 'none' : 'flex' }}
                                                                            className="w-full h-full items-center justify-center text-sm font-bold text-slate-600"
                                                                        >
                                                                            {mc.category_name?.charAt(0) || 'C'}
                                                                        </div>
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <div className="text-sm font-bold truncate text-slate-900 group-hover:text-primary transition-colors">
                                                                            {mc.category_name}
                                                                        </div>
                                                                        <div className="text-xs text-slate-500">
                                                                            {count} Suppliers listed
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2 shrink-0">
                                                                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                                                        {count}
                                                                    </span>
                                                                    {isSelected && (
                                                                        <span className="text-primary font-bold text-sm">✓</span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </>
                            )}

                            {/* Sub Categories Grouped by Letter */}
                            {activeModal === 'sub' && (
                                <>
                                    {totalFilteredCount === 0 ? (
                                        <div className="p-8 text-center text-slate-500 text-sm">
                                            <p className="text-2xl mb-1">🔍</p>
                                            No sub-categories found matching "<span className="text-slate-900 font-semibold">{modalSearch}</span>"
                                        </div>
                                    ) : (
                                        Object.entries(groupedSubCategories).map(([letter, items]) => (
                                            <div key={letter} id={`cat-group-${letter}`} className="space-y-2">
                                                {/* Alphabet Section Header (Borderless Bright) */}
                                                <div className="sticky top-0 z-10 flex items-center gap-2 py-1.5 px-1 bg-white/95 backdrop-blur-md">
                                                    <span className="w-5 h-5 rounded-md bg-amber-500/15 text-amber-700 flex items-center justify-center text-[11px] font-black">
                                                        {letter}
                                                    </span>
                                                    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                                                        {letter} ({items.length})
                                                    </span>
                                                </div>

                                                {/* Items in this letter group */}
                                                <div className="space-y-2">
                                                    {items.map((sc) => {
                                                        const isSelected = String(selectedSubCatId) === String(sc.id);
                                                        const subImg = getCategoryImg(sc.category_img);
                                                        const count = subCategoryCounts[sc.id] ?? 0;

                                                        return (
                                                            <button
                                                                key={sc.id}
                                                                onClick={() => {
                                                                    onSelectSubCategory(sc.id);
                                                                    setActiveModal(null);
                                                                }}
                                                                className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all group ${
                                                                    isSelected
                                                                        ? 'bg-amber-50 border-amber-400 text-slate-900 shadow-sm ring-2 ring-amber-400/30'
                                                                        : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50 hover:border-slate-300 shadow-xs'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-3 min-w-0">
                                                                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-100 shrink-0 border border-slate-200 flex items-center justify-center">
                                                                        {subImg ? (
                                                                            <img
                                                                                src={subImg}
                                                                                alt={sc.product_category_name}
                                                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                                                                onError={(e) => {
                                                                                    e.currentTarget.style.display = 'none';
                                                                                    if (e.currentTarget.nextSibling) {
                                                                                        e.currentTarget.nextSibling.style.display = 'flex';
                                                                                    }
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <div
                                                                            style={{ display: subImg ? 'none' : 'flex' }}
                                                                            className="w-full h-full items-center justify-center text-sm font-bold text-slate-600"
                                                                        >
                                                                            📦
                                                                        </div>
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <div className="text-sm font-bold truncate text-slate-900 group-hover:text-amber-700 transition-colors">
                                                                            {sc.product_category_name}
                                                                        </div>
                                                                        {sc.description ? (
                                                                            <div className="text-xs text-slate-500 truncate max-w-sm">
                                                                                {sc.description}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-xs text-slate-500">
                                                                                {count} Listings available
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2 shrink-0">
                                                                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                                                        {count}
                                                                    </span>
                                                                    {isSelected && (
                                                                        <span className="text-amber-600 font-bold text-sm">✓</span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-3.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
                            <span>
                                Showing {totalFilteredCount} items across {availableLetters.length} alphabetical groups
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    if (activeModal === 'main') {
                                        onSelectMainCategory('all');
                                        onSelectSubCategory('all');
                                    } else {
                                        onSelectSubCategory('all');
                                    }
                                    setModalSearch('');
                                    setActiveModal(null);
                                }}
                                className="px-4 py-2 rounded-xl bg-primary hover:bg-primary-600 text-white font-bold flex items-center gap-1.5 transition-all shadow-md active:scale-95 cursor-pointer"
                            >
                                <span>↺</span>
                                <span>
                                    {activeModal === 'main' ? 'Reset to All Categories' : 'Reset to All Subcategories'}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>

            {/* Smooth Breathing Attention Blink Animation Styles */}
            <style jsx>{`
                .category-dropdown-pulse-primary {
                    animation: smoothGlowPrimary 2.4s ease-in-out infinite;
                }
                .category-dropdown-pulse-amber {
                    animation: smoothGlowAmber 2.4s ease-in-out infinite;
                    animation-delay: 0.3s;
                }
                @keyframes smoothGlowPrimary {
                    0%, 100% {
                        border: 1.5px solid rgba(59, 130, 246, 0.35);
                        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
                    }
                    50% {
                        border: 1.5px solid rgba(59, 130, 246, 0.95);
                        box-shadow: 0 0 14px 2px rgba(59, 130, 246, 0.45);
                    }
                }
                @keyframes smoothGlowAmber {
                    0%, 100% {
                        border: 1.5px solid rgba(245, 158, 11, 0.35);
                        box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
                    }
                    50% {
                        border: 1.5px solid rgba(245, 158, 11, 0.95);
                        box-shadow: 0 0 14px 2px rgba(245, 158, 11, 0.45);
                    }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scaleUp {
                    from { opacity: 0; transform: scale(0.96) translateY(8px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-scaleUp {
                    animation: scaleUp 0.2s cubic-bezier(0.16, 1, 0.3, 1);
                }
            `}</style>
        </section>
    );
}
